import { Component, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { OrderTermsWithCartListJeevesComponent } from 'gung-standard-jeeves';
import {
  AuthService,
  Availability,
  AvailabilityService,
  CartConfigService,
  CartRow,
  CartService,
  Customer,
  DeliveryDateService,
  GungModalService,
  NavbarCartItem,
  PriceService,
  Product,
  ProductService,
  SalesCartConfigService,
  SelectedCustomerService,
  TableRecord
} from 'gung-standard';
import { DatePipe } from '@angular/common';
import { FormBuilder } from '@angular/forms';
import { Subject, distinctUntilChanged, forkJoin, of, switchMap } from 'rxjs';
import { first, map, takeUntil } from 'rxjs';
import { TyloFreightCostService } from '../../../../services/tylo-freight-cost.service';
import { TyloCustomerUtilsService } from '../../../../services/utils/tylo-customer-utils.service';
import { compareAsc, parse } from 'date-fns';
import { TyloJeevesCartListStateService } from '../../../../services/tylo-jeeves-cart-list-state-service';

@Component({
  selector: 'tylo-tylo-order-terms-with-cart-list-jeeves',
  templateUrl: './tylo-order-terms-with-cart-list-jeeves.component.html',
  styleUrls: ['./tylo-order-terms-with-cart-list-jeeves.component.css']
})
export class TyloOrderTermsWithCartListJeevesComponent extends OrderTermsWithCartListJeevesComponent implements OnInit {
  protected cartListIsLoading = false;
  private freightCostProductId = '00000023'; // Uppercase is important
  public shouldFreightBeApplied: boolean;
  public tempFreightEnabled: boolean = false;
  cartCheck: { [key: string]: number } = {};

  hasDimensionOver1500 = false;

  // NOTE: CAN CHANGE FOR DIFERENT CLIENTS
  protected cartRowExtraDeliveryPlan = '_deliveryPlan';

  partDeliveryEntries = [
    { id: '', name: 'SELECT_DELIVERY' },
    { id: '0', name: 'OH_DELLEVTILLATEN_0' },
    { id: '10', name: 'OH_DELLEVTILLATEN_10' }
  ];

  selectDelivery = true;
  avs: Availability[];

  previousProductQuantities: { [s: string]: number } = {};
  public filterMetadataUpdate: Subject<void> = new Subject<void>();

  cartHasDangerousProducts: boolean = false;

  protected tempFreightArticlesIds: string[] = [
    '95109104',
    '95109106',
    '95109102',
    '95109100',
    '95109114',
    '95109116',
    '95109112',
    '95109110'
  ];
  public hasTempFreightArticles: boolean = false;
  public partialDeliveryPopupShown: boolean = false;

  constructor(
    protected authService: AuthService,
    protected formBuilder: FormBuilder,
    protected datePipe: DatePipe,
    protected availabilityService: AvailabilityService,
    protected cartService: CartService,
    protected deliveryDateService: DeliveryDateService,
    protected selectedCustomerService: SelectedCustomerService,
    public salesCartConfigService: SalesCartConfigService,
    public cartConfigService: CartConfigService,
    protected productService: ProductService,
    protected translateService: TranslateService,
    protected priceService: PriceService,
    protected tyloFreightCostService: TyloFreightCostService,
    protected tyloCustomerUtilsService: TyloCustomerUtilsService,
    protected tyloJeevesCartListStateService: TyloJeevesCartListStateService,
    protected gungModalService: GungModalService
  ) {
    super(
      authService,
      formBuilder,
      datePipe,
      availabilityService,
      cartService,
      deliveryDateService,
      selectedCustomerService,
      salesCartConfigService,
      cartConfigService,
      productService
    );
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.tyloJeevesCartListStateService.cartListIsLoading.pipe(takeUntil(this.unsubscribe)).subscribe(isLoading => {
      this.cartListIsLoading = isLoading;
    });
  }

  getData() {
    this.cartService
      .getCurrentCart()
      .pipe(
        takeUntil(this.unsubscribe),
        switchMap(cartRows =>
          forkJoin([
            of(cartRows),
            this.avs
              ? of(this.avs)
              : this.availabilityService
                  .getAvailabilities(
                    cartRows.map(r => r.productId),
                    undefined,
                    true
                  )
                  .pipe(first()),
            this.deliveryDateService.getDeliveryDates(this.form.get('levsattkod').value).pipe(first()),
            this.selectedCustomerService.getSelectedCustomer().pipe(first()),
            this.authService.getRoles().pipe(first()),
            this.productService.getFullProductsByIds(cartRows.map(row => row.productId)).pipe(first())
          ])
        )
      )
      .subscribe(([cartRows, avs, dates, customer, roles, products]) => {
        this.hasTempFreightArticles = cartRows.some(row => this.tempFreightArticlesIds.includes(row.productId));
        this.avs = avs;
        this.cartRows = JSON.parse(JSON.stringify(cartRows));
        this.currentCustomer = customer;
        this.dates = dates;
        this.isUser = roles.length === 1 && roles.includes('USER');

        if (
          (customer.extra.kus.momskod !== 7 || customer.extra.kus.momskod !== 9) &&
          this.form.get('dellevtillaten').value === '0' &&
          !this.partialDeliveryPopupShown
        ) {
          this.partialDeliveryPopupShown = true;
          this.gungModalService
            .openBasicModal(
              this.translateService.instant('OH_DELLEVTILLATEN_0'),
              this.translateService.instant('PARTIAL_DELIVERY_POPUP_TEXT')
            )
            .then(
              () => {},
              () => {}
            );
        }

        this.calulcateOrderWeight(products);
        this.calulcateOrderVolume(products);
        this.checkCartHasDangerousProducts(products);
        this.hasDimensionOver1500 = this.calculateIfLengthExceeds(1500, products);
        this.addTempFreightProduct();
        this.calculateFreightCost();
        this.calculateAdminFee();
        // Calculate delivery date when delivery method changes
        this.form
          .get('levsattkod')
          .valueChanges.pipe(distinctUntilChanged())
          .subscribe(value => {
            this.handlePartDeliveryChange();
            this.onDeliveryMethodChanged(value);
          });

        let isSameCartValues = this.checkUpdateValues();
        if (this.firstLoad || !isSameCartValues) {
          this.firstLoad = !this.firstLoad;
          this.handlePartDeliveryChange();
        }

        avs = avs.map(av => ({ ...av, availabilities: av.extra.availabilities }));
        const nowDate = new Date();
        let minDate = nowDate;
        for (const row of this.cartRows) {
          // if (row.extra.orp.ordberlevdat) {
          //   const tempDate = new Date(row.extra.orp.ordberlevdat);
          //   if (minDate < tempDate) {
          //     minDate = tempDate;
          //   }
          // } else {
          const av = avs.find(a => a.productId === row.productId);
          if (av) {
            const checkQty = this.checkout.extra.oh.dellevtillaten !== '10' ? undefined : row.qty;
            const tempDate = this.getMinDate(av, checkQty);
            if (minDate < tempDate) {
              minDate = tempDate;
            }
          }
        }
        this.minDate = minDate;
        if (this.checkout.extra.oh.ordberlevdat && this.minDate > new Date(this.checkout.extra.oh.ordberlevdat)) {
          const dString: string = this.datePipe.transform(this.minDate, 'yyyy-MM-dd');
          this.checkout.extra.oh.ordberlevdat = dString;
        }

        if (this.productQuantitiesChanged(cartRows)) {
          this.filterMetadataUpdate.next();
          for (const row of cartRows) {
            this.previousProductQuantities[row.productId] = row.qty;
          }
        }

        this.loadingComponent = false;
      });
  }

  checkUpdateValues() {
    let sameValues = true;
    for (const row of this.cartRows) {
      if (
        !this.cartCheck?.[row.productId] ||
        (this.cartCheck?.[row.productId] && row.qty !== this.cartCheck[row.productId])
      ) {
        sameValues = false;
      }
      this.cartCheck[row.productId] = row.qty;
    }
    return sameValues;
  }

  protected productQuantitiesChanged(rows: CartRow[]): boolean {
    if (!this.previousProductQuantities) {
      this.previousProductQuantities = {};
    }

    for (const row of rows) {
      if (
        this.previousProductQuantities?.[row.productId] === undefined ||
        this.previousProductQuantities?.[row.productId] === null ||
        this.previousProductQuantities[row.productId] !== row.qty
      ) {
        return true;
      }
    }

    return false;
  }

  protected initForm() {
    this.form = this.formBuilder.group({
      levsattkod: this.formBuilder.control(this.checkout.extra.procargs.levsattkod || ''),
      levvillkkod: this.formBuilder.control(this.checkout.extra.procargs.levvillkkod || ''),
      betkod: this.formBuilder.control(this.checkout.extra.oh.betkod || ''),
      saljare: this.formBuilder.control(this.checkout.extra.procargs.saljare || ''),
      dellevtillaten: this.formBuilder.control(this.checkout.extra.oh.dellevtillaten || '')
      // ordstat: this.formBuilder.control(this.checkout.extra.oh.ordstat || 10),
    });
  }

  addTempFreightProduct(): void {
    forkJoin({
      temporaryFreightConfig: this.tyloFreightCostService.getTemporaryFreightConfigData().pipe(first()),
      selectedCustomer: this.selectedCustomerService.getSelectedCustomer().pipe(first())
    })
      .pipe(first())
      .subscribe(({ temporaryFreightConfig, selectedCustomer }) => {
        let tempFreightQty = 0;

        if (
          temporaryFreightConfig &&
          (temporaryFreightConfig.testCustomerIds.includes(selectedCustomer.id) ||
            temporaryFreightConfig.temporaryAddExtraArticleEnabled)
        ) {
          this.tempFreightEnabled = true;

          this.cartRows.forEach(cartRow => {
            if (temporaryFreightConfig.temporaryAddArticleIfIdsOrdered.includes(cartRow.productId)) {
              tempFreightQty += cartRow.qty;
            }
          });

          if (tempFreightQty > 0) {
            const productId = temporaryFreightConfig.temporaryArticleToAddId;

            let cost;
            this.priceService.getCustomerPrice(productId, selectedCustomer.id).subscribe(price => {
              cost = price.customerNetPrice.value * tempFreightQty;
            });

            const freightCostValue = {
              productId: productId,
              value: cost,
              currencyCode: selectedCustomer.extra.kus.valkod,
              quantity: tempFreightQty
            };

            // add it to extra in checkout to use it in summarize step
            this.checkout.extra.additionalCosts = {
              ...this.checkout.extra.additionalCosts,
              tempFreight: freightCostValue
            };

            // show alert message about the freight cost
            this.alertMessageFreightCost = {
              type: 'success',
              message: this.translateService.instant('TYLO_FREIGHT_COST_WILL_BE_CALCULATED')
            };
          } else {
            this.alertMessageFreightCost = {
              type: 'success',
              message: this.translateService.instant('TYLO_FREIGHT_COST_ERROR')
            };
          }
        }
      });
  }

  calculateFreightCost(): void {
    this.alertMessageFreightCost = null;

    const selectedLevsattkodValue = this.form.get('levsattkod').value || '';

    const fraktkol3limit = Math.max(this.orderWeight, this.orderVolume);

    forkJoin({
      freightCost: this.tyloFreightCostService.getFreightCost(selectedLevsattkodValue, fraktkol3limit).pipe(first()),
      selectedCustomer: this.selectedCustomerService.getSelectedCustomer().pipe(first())
    })
      .pipe(
        first(),
        switchMap(({ freightCost, selectedCustomer }) => {
          this.checkout.extra.additionalCosts = {
            ...this.checkout.extra.additionalCosts,
            freight: null
          };

          this.shouldFreightBeApplied = true;

          return this.tyloFreightCostService.checkIfManualFreightCost(this.cartRows, selectedCustomer.id).pipe(
            first(),
            map(isManual => ({ isManual, freightCost, selectedCustomer }))
          );
        })
      )
      .subscribe(({ isManual, freightCost, selectedCustomer }) => {
        this.checkout.extra.additionalCosts = {
          ...this.checkout.extra.additionalCosts,
          freight: null
        };

        if (isManual) {
          // If manual freight cost. Remove the freight cost from the cart
          this.shouldFreightBeApplied = false;
          freightCost.value = -1;

          this.alertMessageFreightCost = {
            type: 'success',
            message: this.translateService.instant('TYLO_FREIGHT_COST_MANUAL')
          };
          return;
        }

        // Add delivery method = 3 as always freight free if no delivery address change. Just like we have with delivery method 99 now.
        if (!this.checkout.extra._deliveryAddressEdited && this.checkout.extra.procargs.levvillkkod === 3) {
          this.shouldFreightBeApplied = false;
          return;
        }
        // Dont add freight for som customers (Export) or delivery terms (1 - Freight Free)
        if (
          !this.tyloCustomerUtilsService.shouldFreightByApplied(
            selectedCustomer,
            this.checkout.extra.procargs.levvillkkod
          )
        ) {
          this.shouldFreightBeApplied = false;
          return;
        }

        // In case freight cost value is defined
        // Then add to cart item freightCostProductId and show message about freight cost
        // otherwise remove item freightCostProductId from cart if already in the cart
        if (!!freightCost.value && freightCost.value > -1) {
          const freightCostValue = {
            productId: this.freightCostProductId,
            value: freightCost.value,
            currencyCode: selectedCustomer.extra.kus.valkod,
            quantity: 1
          };

          // add it to extra in checkout to use it in summarize step
          this.checkout.extra.additionalCosts = {
            ...this.checkout.extra.additionalCosts,
            freight: freightCostValue
          };

          // show alert message about the freight cost
          this.alertMessageFreightCost = {
            type: 'success',
            message: this.translateService.instant('TYLO_FREIGHT_COST', {
              value: freightCostValue.value,
              currencyCode: freightCostValue.currencyCode
            })
          };
        } else {
          this.alertMessageFreightCost = {
            type: 'success',
            message: this.translateService.instant('TYLO_FREIGHT_COST_ERROR')
          };
        }
      });
  }

  handleNextButtonClicked(): void {
    if (this.form.invalid || this.loadingComponent || this.cartListIsLoading) {
      return;
    }

    this.assignFormValues(this.form);
    if (this.form.get('dellevtillaten').value === '') {
      this.selectDelivery = false;
      return;
    }
    this.stepDone.emit(this.checkout);
  }
  assignRowDates(): void {
    if (!this.cartRows?.length) {
      return;
    }
    /* this.availabilityService
      .getAvailabilities(
        this.cartRows.map(r => r.productId),
        undefined,
        true
      )
      .pipe(map(avs => avs.map(av => ({ ...av, avs: av.extra.avs }))))
      .subscribe(avs => { */
    const avs = this.avs.map(av => ({ ...av, availabilities: av.extra.availabilities }));
    const bulkExtra: {
      productId: string;
      extra: { [s: string]: any };
      targetStockId?: string;
      productPartialId?: string;
      replace?: boolean;
    }[] = [];
    for (let i = 0; i < this.cartRows.length; i++) {
      const row = this.cartRows[i];
      const av = avs.find(a => a.productId === row.productId);
      const newExtra = JSON.parse(JSON.stringify(row.extra));
      newExtra._availability_reference = av;
      let mindate = null;

      if (av) {
        const tempDate = this.getMinDate(av, row.qty);
        const nowDate = new Date();
        let minDate = nowDate;
        if (minDate < tempDate) {
          minDate = tempDate;
        }

        for (const testDate of this.dates) {
          const dString1: string = this.datePipe.transform(minDate, 'yyyy-MM-dd');
          const dString2: string = this.datePipe.transform(testDate.date, 'yyyy-MM-dd');

          // We cant include time when comparing
          if (dString2 >= dString1) {
            if (testDate.valid) {
              minDate = testDate.date;
              break;
            }
          }
        }

        const dString: string = this.datePipe.transform(minDate, 'yyyy-MM-dd');
        mindate = dString;
      }

      newExtra.orp = newExtra.orp || {};
      newExtra.orp.ordberlevdat = newExtra._calc_min_date;
      if (newExtra._user_selected_ordberlevdat) {
        if (newExtra._user_selected_ordberlevdat > newExtra._calc_min_date) {
          newExtra.orp.ordberlevdat = newExtra._user_selected_ordberlevdat;
        }
        if (new Date(newExtra._user_selected_ordberlevdat) > new Date(newExtra._calc_min_date)) {
          // row.extra.orp.ordberlevdat = row.extra._user_selected_ordberlevdat;
          // TODO: Check if the selected date is in the available dates in this delivery method
          // this.deliveryDateService.checkIfDateInDeliveryDates(new Date(row.extra._user_selected_ordberlevdat));
        }
      }
      const deliveryPlan = {};
      const dates = Object.keys(av.availabilities);
      let quantityRemaining = row.qty;
      var quantityAllocated = 0;
      let setMinDate = false;
      for (const date of dates) {
        const qty = av.availabilities[date] - quantityAllocated;
        if (qty > 0 && quantityRemaining > 0) {
          const parsedDate = parse(date, 'yyMMdd', new Date());
          const formattedDate = this.datePipe.transform(parsedDate, 'yyyy-MM-dd');

          let assignedDate = formattedDate;
          if (!setMinDate) {
            if (this.checkout.extra.oh.dellevtillaten !== '10') {
              newExtra._calc_min_date = assignedDate;
            } else {
              newExtra._calc_min_date = mindate;
            }
          }
          setMinDate = true;
          if (newExtra._user_selected_ordberlevdat > assignedDate) {
            assignedDate = newExtra._user_selected_ordberlevdat;
          }
          const currentOnDate = deliveryPlan[assignedDate] || 0;
          const currentAllocation = Math.min(qty, quantityRemaining);

          deliveryPlan[assignedDate] = currentOnDate + currentAllocation;

          quantityRemaining = quantityRemaining - currentAllocation;
          quantityAllocated = quantityAllocated + currentAllocation;
        }
      }
      newExtra.minDate = newExtra._calc_min_date;
      if (quantityRemaining > 0) {
        // Handle edge case of last quantities of expiring product
        deliveryPlan['2099-12-31'] = quantityRemaining;
      }
      // only set delivery plan if is not joint delivery
      if (this.checkout.extra.oh.dellevtillaten !== '10') {
        newExtra[this.cartRowExtraDeliveryPlan] = deliveryPlan;
      } else {
        delete newExtra[this.cartRowExtraDeliveryPlan];
      }
      newExtra._dellevtillaten = this.checkout.extra.oh.dellevtillaten;
      newExtra._levsattkod = this.form.get('levsattkod').value;

      // Code frome setSplitDeliveries();
      const deliveryPlanWitnAvDates = {};
      for (const key in row.extra._deliveryPlan) {
        if (Object.prototype.hasOwnProperty.call(row.extra._deliveryPlan, key)) {
          const avData = row.extra._deliveryPlan[key];
          const planDate = new Date(key);
          let findFirstAvDate = this.dates.find(date => {
            return date.valid && compareAsc(date.date, planDate) >= 0;
          });
          if (!findFirstAvDate) {
            findFirstAvDate = {
              date: new Date('2099-12-31'),
              valid: true,
              invalidReason: null
            };
          }
          const formattedDate = this.datePipe.transform(findFirstAvDate.date, 'yyyy-MM-dd');
          deliveryPlanWitnAvDates[formattedDate] = avData;
        }
      }
      newExtra._deliveryPlan = deliveryPlanWitnAvDates;

      bulkExtra.push({
        productId: row.productId,
        productPartialId: row.productPartialId,
        targetStockId: row.targetStockId,
        extra: newExtra
      });
    }

    this.loadingComponent = false;
    this.cartService.bulkSetExtra(bulkExtra);
    // this.setSplitDeliveries();
    /*       }); */
  }

  setSplitDeliveries() {
    this.deliveryDateService.getDeliveryDates(this.checkout.extra.oh.dellevtillaten).subscribe(dates => {
      for (var i = 0; i > this.cartRows.length; i++) {
        const row = this.cartRows[i];
        const deliveryPlanWitnAvDates = {};
        for (const key in row.extra._deliveryPlan) {
          if (Object.prototype.hasOwnProperty.call(row.extra._deliveryPlan, key)) {
            const avData = row.extra._deliveryPlan[key];
            const planDate = new Date(key);
            const findFirstAvDate = this.dates.find(date => {
              return date.valid && compareAsc(date.date, planDate) >= 0;
            });
            const formattedDate = this.datePipe.transform(findFirstAvDate.date, 'yyyy-MM-dd');
            deliveryPlanWitnAvDates[formattedDate] = avData;
          }
        }
        this.cartService.removeExtraField(row.productId, '_deliveryPlan');
        const extra = {
          ...row.extra,
          _deliveryPlan: deliveryPlanWitnAvDates
        };
        this.cartService.setExtra(row.productId, extra);
      }
    });
  }

  createFullDeliveryPlan() {
    const sDate = this.checkout.extra.oh.ordberlevdat;
    const bulkExtra: {
      productId: string;
      extra: { [s: string]: any };
      targetStockId?: string;
      productPartialId?: string;
      replace?: boolean;
    }[] = [];
    for (var i = 0; i < this.cartRows.length; i++) {
      const row = this.cartRows[i];
      // row.deliveryDate = sDate;
      row.extra.orp = row.extra.orp || {};
      row.extra.orp.ordberlevdat = sDate;
      const deliveryPlan = {};
      deliveryPlan[sDate] = row.qty;
      // This is important because used by backend to split lines
      if (this.checkout.extra.oh.dellevtillaten === '10') {
        row.extra[this.cartRowExtraDeliveryPlan] = deliveryPlan;
      } else {
        delete row.extra[this.cartRowExtraDeliveryPlan];
      }
      row.extra._dellevtillaten = this.checkout.extra.oh.dellevtillaten;
      row.extra._levsattkod = this.form.get('levsattkod').value;

      bulkExtra.push({
        productId: row.productId,
        productPartialId: row.productPartialId,
        targetStockId: row.targetStockId,
        extra: {
          _deliveryPlan: row.extra._deliveryPlan,
          _dellevtillaten: row.extra._dellevtillaten,
          _levsattkod: row.extra._levsattkod
        },
        replace: true
      });
    }
    this.cartService.bulkSetExtra(bulkExtra);
  }

  /**
   * calculateAdminFee - Check min value and add admin charge
   */
  calculateAdminFee() {
    this.checkout.extra.additionalCosts = {
      ...this.checkout.extra.additionalCosts,
      adminFee: null
    };

    // 20201211 - NEVER ADD THE ADMIN FEE
    return;

    this.cartService
      .getCurrentCart()
      .pipe(first())
      .subscribe(cart => {
        const idQtyMap = cart.reduce((acc, curr) => ({ ...acc, [curr.productId]: curr.qty }), {});
        const ids = Object.keys(idQtyMap);
        forkJoin({
          rows: of(cart),
          products: ids.length > 0 ? this.productService.getFullProductsByIds(ids).pipe(first()) : of([]),
          prices: this.priceService.getCartRowPricesObservable(cart).pipe(first())
        })
          .pipe(
            first(),
            map(({ rows, products, prices }) => {
              const toReturnRows = rows.map(row => {
                const rowProduct = products.find(p => p.id === row.productId);
                const rowPrice = prices.find(r => r.productId === row.productId);
                if (!rowProduct || !rowPrice) {
                  throw new Error('Could not find mapped product or price');
                }
                const data: NavbarCartItem = {
                  product: rowProduct,
                  price: rowPrice,
                  cartRow: row
                };
                return data;
              });
              const totalValue = toReturnRows
                .map(row => row.price.cartRowUnitPriceInclRowDiscount.value * row.cartRow.qty)
                .reduce((acc, curr) => acc + curr, 0);
              const currency = prices.length > 0 ? prices[0].customerNetPrice.currencyCode : '';

              switch (currency) {
                case 'SEK':
                  if (totalValue < 300) {
                    this.alertMessageAdminFee = {
                      type: 'warning',
                      message: this.translateService.instant('TYLO_MIN_ORDER_ADMIN_FEE', {
                        min: 300 + currency,
                        fee: 50 + currency
                      })
                    };
                    // add it to extra in checkout to use it in summarize step
                    this.checkout.extra.additionalCosts = {
                      ...this.checkout.extra.additionalCosts,
                      adminFee: {
                        min: 300,
                        fee: 50,
                        currency
                      }
                    };
                  }
                  break;
                case 'EUR':
                case 'USD':
                case 'GBP':
                  if (totalValue < 30) {
                    this.alertMessageAdminFee = {
                      type: 'warning',
                      message: this.translateService.instant('TYLO_MIN_ORDER_ADMIN_FEE', {
                        min: 30 + currency,
                        fee: 5 + currency
                      })
                    };
                    // add it to extra in checkout to use it in summarize step
                    this.checkout.extra.additionalCosts = {
                      ...this.checkout.extra.additionalCosts,
                      adminFee: {
                        min: 30,
                        fee: 5,
                        currency
                      }
                    };
                  }
                  break;
              }
            })
          )
          .subscribe();
      });
  }

  handlePartDeliveryChange() {
    localStorage.setItem(
      'deliveryChoice',
      this.form.get('dellevtillaten')?.value || this.checkout.extra.oh.dellevtillaten || ''
    );
    if (this.form.get('dellevtillaten').value !== '') {
      this.selectDelivery = true;
    } else {
      this.selectDelivery = false;
    }
    this.loadingComponent = true;
    this.checkout.extra.oh.dellevtillaten = this.form.get('dellevtillaten')?.value || '';
    if (this.checkout.extra.oh.dellevtillaten === '0') {
      delete this.checkout.extra.oh.ordberlevdat;
      this.assignRowDates();
    } else {
      this.partialDeliveryPopupShown = false;
      delete this.minDate;
      const requestAvs = this.checkAllProductsInAvailabilities(
        this.cartRows.map(r => r.productId),
        this.availabilities
      )
        ? of(Object.values(this.availabilities))
        : this.availabilityService.getAvailabilities(
            this.cartRows.map(r => r.productId),
            undefined,
            true
          );

      requestAvs
        .pipe(map(avs => avs.map(av => ({ ...av, availabilities: av.extra.availabilities }))))
        .subscribe(avs => {
          const nowDate = new Date();
          let minDateFromAvailability = nowDate;

          // let maxAssignedDate = '1970-01-01';
          for (const row of this.cartRows) {
            const av = avs.find(a => a.productId === row.productId);
            if (av) {
              const tempDate = this.getMinDate(av, row.qty);
              if (minDateFromAvailability < tempDate) {
                minDateFromAvailability = tempDate;
              }
            }
            // if (row.extra._user_selected_ordberlevdat && row.extra._user_selected_ordberlevdat > maxAssignedDate) {
            //   maxAssignedDate = row.extra._user_selected_ordberlevdat;
            // }
          }
          this.minDate = minDateFromAvailability;
          for (const testDate of this.dates) {
            if (testDate.date >= this.minDate) {
              if (testDate.valid) {
                this.minDate = testDate.date;
                break;
              }
            }
          }

          const dString: string = this.datePipe.transform(this.minDate, 'yyyy-MM-dd');

          this.checkout.extra.oh.ordberlevdat = dString;

          this.loadingComponent = false;
          this.createFullDeliveryPlan();
        });
    }
  }

  optionsFilterDeliveryMethod(customer: Customer, option: { key: string; value: string | TableRecord }): boolean {
    let allowedDeliveryMethod = [];
    this.checkout.extra.customerVATCode = this.tyloCustomerUtilsService.getCustomerVATCode(customer);
    if (this.checkout.extra.customerVATCode === 7) {
      allowedDeliveryMethod.push(...['80', '83', '81', '75']);
    } else if (this.checkout.extra.customerVATCode === 9) {
      allowedDeliveryMethod.push(...['85', '88', '86', '75']);
    } else {
      if (!this.hasDimensionOver1500) {
        if (this.orderVolume <= 0.071 || this.orderWeight <= 20) {
          allowedDeliveryMethod.push(...['66']);
        }
        if (this.orderWeight <= 35 || this.orderVolume <= 0.125) {
          allowedDeliveryMethod.push(...['67']);
        }
      }

      if (this.orderWeight <= 2400 && this.orderVolume <= 8 * 280) {
        allowedDeliveryMethod.push(...['68', '69']);
      }
    }
    if (
      customer.extra.kus.levsattkod &&
      allowedDeliveryMethod.findIndex(d => d.id === customer.extra.kus.levsattkod + '') === -1
    ) {
      allowedDeliveryMethod.push(customer.extra.kus.levsattkod + '');
    }

    if (this.checkout.extra.customerVATCode === 1 && this.cartHasDangerousProducts) {
      allowedDeliveryMethod = allowedDeliveryMethod.filter(a => !['66', '67'].includes(a));
    } else if (
      (this.checkout.extra.customerVATCode === 7 || this.checkout.extra.customerVATCode === 9) &&
      this.cartHasDangerousProducts
    ) {
      allowedDeliveryMethod = allowedDeliveryMethod.filter(a => !['75', '81', '86'].includes(a));
    }

    return allowedDeliveryMethod.includes(option.key);
  }

  onDeliveryMethodChanged(event) {
    const bulkExtra: {
      productId: string;
      extra: { [s: string]: any };
      targetStockId?: string;
      productPartialId?: string;
      replace?: boolean;
    }[] = [];
    for (const row of this.cartRows) {
      bulkExtra.push({
        productId: row.productId,
        productPartialId: row.productPartialId,
        targetStockId: row.targetStockId,
        extra: {
          ...row.extra,
          _levsattkod: event
        },
        replace: true
      });
    }

    this.cartService.bulkSetExtra(bulkExtra);
  }

  calculateIfLengthExceeds(length: number, products: Product[]) {
    for (const product of products) {
      if (
        !(
          product.extra.ar?.q_ar_hojd <= 1500 &&
          product.extra.ar?.q_ar_bredd <= 500 &&
          product.extra.ar?.q_ar_langd <= 500
        )
      ) {
        return true;
      }

      if (
        !(
          product.extra.ar?.q_ar_bredd <= 1500 &&
          product.extra.ar?.q_ar_langd <= 500 &&
          product.extra.ar?.q_ar_hojd <= 500
        )
      ) {
        return true;
      }

      if (
        !(
          product.extra.ar?.q_ar_langd <= 1500 &&
          product.extra.ar?.q_ar_hojd <= 500 &&
          product.extra.ar?.q_ar_bredd <= 500
        )
      ) {
        return true;
      }
    }
    return false;
  }

  checkCartHasDangerousProducts(products: Product[]): void {
    if (products.some(p => p.extra?.ar?.q_ic_dgd == 1)) {
      this.cartHasDangerousProducts = true;
      this.filterMetadataUpdate.next();
    }
  }
}
